import { useEffect, useRef, useState } from 'react';
import { Link2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { detectInAppBrowser, type InAppBrowser } from '@/utils/detectInAppBrowser';
import { useToast } from '@/hooks/use-toast';

interface Props {
  url?: string;
  className?: string;
  /** Full-screen blocker for critical flows (checkout, signup). */
  blocking?: boolean;
}

const LABELS: Record<NonNullable<InAppBrowser>, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  snapchat: 'Snapchat',
  twitter: 'X',
  other: 'această aplicație',
};

function openExternally(targetUrl: string) {
  const ua = navigator.userAgent;
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const stripped = targetUrl.replace(/^https?:\/\//, '');

  if (isAndroid) {
    const intent =
      `intent://${stripped}#Intent;scheme=https;` +
      `package=com.android.chrome;` +
      `S.browser_fallback_url=${encodeURIComponent(targetUrl)};end`;
    window.location.href = intent;
    return;
  }

  if (isIOS) {
    const chromeIOS = targetUrl.replace(/^https?:\/\//, 'googlechromes://');
    const w = window.open(chromeIOS, '_blank');
    if (!w) window.location.href = targetUrl;
    return;
  }

  window.open(targetUrl, '_blank', 'noopener,noreferrer');
}

/**
 * Minimal TikTok-style bridge screen for in-app browsers (FB/IG/etc.).
 * On Android: auto-triggers Chrome intent once, then shows a big "Deschide" button.
 * On iOS: shows link + Open button + short manual instructions (Apple blocks auto-open).
 */
export function InAppBrowserWarning({ url, className, blocking }: Props) {
  const [browser, setBrowser] = useState<InAppBrowser>(null);
  const [copied, setCopied] = useState(false);
  const autoTriedRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    setBrowser(detectInAppBrowser());
  }, []);

  useEffect(() => {
    if (!blocking || !browser) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [blocking, browser]);

  // Auto-open on Android (Chrome intent is reliable). iOS cannot auto-open.
  useEffect(() => {
    if (!browser || autoTriedRef.current) return;
    const isAndroid = /Android/i.test(navigator.userAgent);
    if (!isAndroid) return;
    autoTriedRef.current = true;
    const targetUrl = url ?? window.location.href;
    // Small delay so React finishes mount before we navigate away.
    const t = setTimeout(() => openExternally(targetUrl), 250);
    return () => clearTimeout(t);
  }, [browser, url]);

  if (!browser) return null;

  const targetUrl = url ?? (typeof window !== 'undefined' ? window.location.href : '');
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const label = LABELS[browser];
  const browserName = isIOS ? 'Safari' : 'Chrome';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(targetUrl);
      setCopied(true);
      toast({ title: 'Link copiat', description: `Lipește-l în ${browserName}.` });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ title: 'Nu am putut copia', description: targetUrl, variant: 'destructive' });
    }
  };

  const handleOpen = () => openExternally(targetUrl);

  const content = (
    <div className={`flex flex-col items-center text-center px-6 ${className ?? ''}`}>
      <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Link2 className="h-7 w-7 text-muted-foreground" strokeWidth={1.75} />
      </div>

      <p className="text-base font-medium text-foreground break-all mb-3 max-w-sm">
        {targetUrl.replace(/^https?:\/\//, '')}
      </p>

      <p className="text-sm text-muted-foreground max-w-sm mb-8 leading-relaxed">
        {label} nu poate deschide această pagină corect. Deschide-o în {browserName} pentru a continua.
      </p>

      <Button
        size="lg"
        onClick={handleOpen}
        className="w-full max-w-sm h-12 text-base font-semibold rounded-xl"
      >
        Deschide în {browserName}
      </Button>

      <button
        onClick={handleCopy}
        className="mt-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copied ? 'Link copiat' : 'Copiază linkul'}
      </button>

      {isIOS && (
        <p className="mt-6 text-xs text-muted-foreground max-w-xs leading-relaxed">
          Dacă butonul nu deschide Safari, apasă <strong>„⋯"</strong> {browser === 'facebook' ? 'dreapta-jos' : 'dreapta-sus'} și alege <strong>„Open in external browser"</strong>.
        </p>
      )}
    </div>
  );

  if (blocking) {
    return (
      <div
        className="fixed inset-0 z-[9999] bg-background overflow-y-auto flex items-center justify-center py-12"
        role="dialog"
        aria-modal="true"
      >
        {content}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border bg-card py-8">
      {content}
    </div>
  );
}

export default InAppBrowserWarning;
